import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  type QueryKey,
} from "@tanstack/react-query";
import type {
  PreflightPlatformCode,
  PreflightReportSortField,
  PreflightReportSortOrder,
} from "@nexus/contracts";
import {
  getCreativePreviews,
  getInsights,
  getReport,
  getReportStatus,
  listMarketCatalogue,
  listMarkets,
  listReports,
} from "@/features/preflight/api/preflight.api";
import {
  isTerminalReportStatus,
  type PreflightReportStatusResult,
} from "@/features/preflight/types/preflight.types";
import {
  PREFLIGHT_POLL_FAST_MS,
  PREFLIGHT_POLL_FAST_WINDOW_MS,
  PREFLIGHT_POLL_MED_MS,
  PREFLIGHT_POLL_MED_WINDOW_MS,
  PREFLIGHT_POLL_SLOW_MS,
  PREFLIGHT_PREVIEW_STALE_MS,
} from "@/lib/query-constants";

export interface PreflightReportsListParams {
  search?: string;
  platform?: PreflightPlatformCode;
  /** Null or absent means all-markets scope (R7). */
  market?: string | null;
  /** Absent means the server's default newest-first ordering. */
  sortBy?: PreflightReportSortField;
  sortOrder?: PreflightReportSortOrder;
  /** True narrows the list to the signed-in user's starred reports. */
  favoritesOnly?: boolean;
}

/** Matches the tenant segment used by preflight cache keys — reports are tenant-scoped. */
export function normalizedPreflightTenantId(
  tenantId: string | null | undefined,
): string {
  const tid = tenantId ?? "";
  return tid || "__none__";
}

/** Cache-key segment for the all-markets scope, so it never collides with a market id. */
const ALL_MARKETS_SCOPE = "__all__";

function normalizedMarketScope(marketId: string | null | undefined): string {
  return marketId || ALL_MARKETS_SCOPE;
}

export const preflightKeys = {
  all: ["preflight"] as const,
  lists: () => ["preflight", "reports", "list"] as const,
  /** Every list scope for one tenant — a cache-write prefix that cannot cross tenants. */
  listsForTenant: (tenantId: string) =>
    ["preflight", "reports", "list", tenantId] as const,
  list: (tenantId: string, params: PreflightReportsListParams) =>
    ["preflight", "reports", "list", tenantId, params] as const,
  markets: (tenantId: string) => ["preflight", "markets", tenantId] as const,
  catalogue: () => ["preflight", "market-catalogue"] as const,
  /** Prefix covering every market scope for a tenant — the invalidation target. */
  insightsScopes: (tenantId: string) => ["preflight", "insights", tenantId] as const,
  insights: (tenantId: string, marketId: string | null | undefined) =>
    ["preflight", "insights", tenantId, normalizedMarketScope(marketId)] as const,
  /** Keyed by the creatives asked for, so each opened rail panel is cached on its own. */
  creativePreviews: (tenantId: string, assetIds: readonly string[]) =>
    ["preflight", "creative-previews", tenantId, [...assetIds].sort()] as const,
  details: () => ["preflight", "reports", "detail"] as const,
  detail: (tenantId: string, id: string) =>
    ["preflight", "reports", "detail", tenantId, id] as const,
  /**
   * `pollStartedAt` is wall-clock ms when polling began; baked into the key so the
   * relaxing backoff ramp has a stable anchor (a fresh session restarts the ramp).
   */
  status: (tenantId: string, id: string, pollStartedAt = 0) =>
    ["preflight", "reports", "status", tenantId, id, pollStartedAt] as const,
} as const;

const LIST_PARAMS_KEY_INDEX = 4;

/**
 * Read the filters back off a list key, so cache writers can tell which scope a
 * cached list holds without indexing into the key themselves.
 */
export function preflightListKeyParams(
  key: QueryKey,
): PreflightReportsListParams | undefined {
  return key[LIST_PARAMS_KEY_INDEX] as PreflightReportsListParams | undefined;
}

const REPORTS_PAGE_SIZE = 20;

/** Cursor-paginated reports list with server-side search, platform and market filters. */
export function preflightReportsInfiniteQueryOptions(
  tenantId: string | null | undefined,
  params: PreflightReportsListParams = {},
) {
  const tid = normalizedPreflightTenantId(tenantId);
  const { platform, sortBy, sortOrder } = params;
  const search = params.search?.trim() || undefined;
  const market = params.market || undefined;
  const favoritesOnly = params.favoritesOnly || undefined;
  // Sort is part of the key: changing it starts a new paginated list rather than
  // appending differently-ordered pages to the one already loaded.
  return infiniteQueryOptions({
    queryKey: preflightKeys.list(tid, {
      search,
      platform,
      market,
      sortBy,
      sortOrder,
      favoritesOnly,
    }),
    enabled: !!tenantId,
    queryFn: ({ pageParam }) =>
      listReports({
        cursor: pageParam,
        limit: REPORTS_PAGE_SIZE,
        search,
        platform,
        market,
        sortBy,
        sortOrder,
        favoritesOnly,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

/** The tenant's market list, shared by every selector and the manage panel. */
export function preflightMarketsQueryOptions(
  tenantId: string | null | undefined,
) {
  const tid = normalizedPreflightTenantId(tenantId);
  return queryOptions({
    queryKey: preflightKeys.markets(tid),
    queryFn: listMarkets,
    enabled: !!tenantId,
  });
}

/** Active DB catalogue identities for the Pre Flight add-market picker. */
export function preflightMarketCatalogueQueryOptions() {
  return queryOptions({
    queryKey: preflightKeys.catalogue(),
    queryFn: listMarketCatalogue,
    staleTime: 1000 * 60 * 60,
    meta: { skipGlobalError: true },
  });
}

/**
 * Market-scoped aggregate behind the hub dashboard. Scope is part of the key, so
 * switching markets is a cache read rather than a manual refetch (R8).
 */
export function preflightInsightsQueryOptions(
  tenantId: string | null | undefined,
  marketId: string | null | undefined,
) {
  const tid = normalizedPreflightTenantId(tenantId);
  const market = marketId || undefined;
  return queryOptions({
    queryKey: preflightKeys.insights(tid, market),
    queryFn: () => getInsights({ market }),
    enabled: !!tenantId,
  });
}

/**
 * Signed thumbnails for the creatives a rail panel is showing. Kept out of the aggregate
 * because read URLs expire: they are minted per panel and refetched when they go stale.
 */
export function preflightCreativePreviewsQueryOptions(
  tenantId: string | null | undefined,
  assetIds: readonly string[],
) {
  const tid = normalizedPreflightTenantId(tenantId);
  return queryOptions({
    queryKey: preflightKeys.creativePreviews(tid, assetIds),
    queryFn: () => getCreativePreviews(assetIds),
    enabled: !!tenantId && assetIds.length > 0,
    // Refetch rather than serve a URL that has since expired (5 min server-side TTL).
    staleTime: PREFLIGHT_PREVIEW_STALE_MS,
  });
}

/** Full report detail. */
export function preflightReportQueryOptions(
  tenantId: string | null | undefined,
  id: string,
) {
  const tid = normalizedPreflightTenantId(tenantId);
  return queryOptions({
    queryKey: preflightKeys.detail(tid, id),
    queryFn: () => getReport(id),
    enabled: !!tenantId && !!id,
  });
}

/**
 * Resolve the relaxing-backoff interval from elapsed-since-poll-start.
 * Exported for unit testing the ramp boundaries in isolation.
 */
export function preflightPollInterval(
  status: PreflightReportStatusResult["status"] | undefined,
  pollStartedAt: number,
  now: number = Date.now(),
): number | false {
  if (!pollStartedAt) return false;
  if (status && isTerminalReportStatus(status)) return false;
  const elapsed = now - pollStartedAt;
  if (elapsed < PREFLIGHT_POLL_FAST_WINDOW_MS) return PREFLIGHT_POLL_FAST_MS;
  if (elapsed < PREFLIGHT_POLL_MED_WINDOW_MS) return PREFLIGHT_POLL_MED_MS;
  return PREFLIGHT_POLL_SLOW_MS;
}

/**
 * Per-report status poll. Stops on any terminal status; otherwise refetches on a
 * relaxing backoff anchored to `pollStartedAt` (modeled on `exportStatusQueryOptions`,
 * not `bulkJobStatusQueryOptions` whose `dataUpdatedAt` resets every poll).
 * Enabled only when `pollStartedAt > 0`, so terminal rows never subscribe.
 */
export function preflightReportStatusQueryOptions(
  tenantId: string | null | undefined,
  id: string,
  pollStartedAt: number,
) {
  const tid = normalizedPreflightTenantId(tenantId);
  return queryOptions<PreflightReportStatusResult>({
    queryKey: preflightKeys.status(tid, id, pollStartedAt),
    queryFn: () => getReportStatus(id),
    enabled: !!tenantId && !!id && pollStartedAt > 0,
    refetchInterval: (query) =>
      preflightPollInterval(query.state.data?.status, pollStartedAt),
    meta: { skipGlobalError: true },
  });
}
